import { FeatureTariffInformation } from "./FeatureTariffInformation";
import { createFeatureLockTranslator } from "./featureLockText";
import { CheckCircleOutlined, LockOutlined, RocketOutlined } from "@ant-design/icons";
import { Button, Space, Tag, Typography, theme } from "../ui";

import { ThemedModal } from "../components/ThemedModal";
import { spacing, typography } from "../layout/spacing";
import type { FeatureLockModalProps } from "./featureLockTypes";

const { Paragraph, Text } = Typography;

export function FeatureLockModal({
  open,
  onClose,
  feature,
  onUpgrade,
  upgradeLabel,
  cancelLabel,
  benefitsTitle,
  tr,
  language,
}: FeatureLockModalProps) {
  const { token } = theme.useToken();

  if (!feature) return null;

  const translate = createFeatureLockTranslator(tr, language);

  const resolvedUpgradeLabel = upgradeLabel ?? translate("To plan selection", "zur Tarifauswahl");
  const resolvedCancelLabel = cancelLabel ?? translate("Cancel", "abbrechen");
  const resolvedBenefitsTitle = benefitsTitle ?? translate(
    "Included in this module:",
    "im Modul enthalten:"
  );

  const handleUpgrade = () => {
    onClose();
    if (onUpgrade) {
      onUpgrade();
    } else if (feature.upgradeUrl && typeof window !== "undefined") {
      window.location.href = feature.upgradeUrl;
    }
  };

  return (
    <ThemedModal
      open={open}
      onCancel={onClose}
      centered
      width={540}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: spacing.tightGap, flexWrap: "wrap" }}>
          <LockOutlined style={{ color: token.colorTextSecondary }} />
          <span>{feature.title}</span>
          {feature.badgeText && (
            <Tag color="blue" style={{ marginInlineStart: spacing.microGap }}>
              {feature.badgeText}
            </Tag>
          )}
        </div>
      }
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: spacing.tightGap }}>
          <Button onClick={onClose}>
            {resolvedCancelLabel}
          </Button>
          <Button type="primary" icon={<RocketOutlined />} onClick={handleUpgrade}>
            {resolvedUpgradeLabel}
          </Button>
        </div>
      }
    >
      <div style={{ paddingBlock: spacing.tightGap }}>
        <Paragraph style={{ fontSize: typography.base, color: token.colorTextSecondary, marginBottom: spacing.elementGap }}>
          {feature.description}
        </Paragraph>

        {feature.highlights && feature.highlights.length > 0 && (
          <div style={{ marginBottom: spacing.elementGap }}>
            <Text strong style={{ display: "block", marginBottom: spacing.tightGap, fontSize: typography.base }}>
              {resolvedBenefitsTitle}
            </Text>
            <Space direction="vertical" size={spacing.tightGap} style={{ width: "100%" }}>
              {feature.highlights.map((h, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: spacing.tightGap }}>
                  <CheckCircleOutlined style={{ color: token.colorSuccess, marginTop: spacing.microGap, flexShrink: 0 }} />
                  <Text style={{ fontSize: typography.base }}>{h}</Text>
                </div>
              ))}
            </Space>
          </div>
        )}

        <div
          style={{
            background: token.colorFillAlter,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadiusLG,
            padding: `${spacing.tightGap}px ${spacing.elementGap}px`,
          }}
        >
          <Text strong style={{ display: "block", marginBottom: spacing.microGap, fontSize: typography.base }}>
            {translate("Tariff and booking information", "Tarif- und Buchungsinformation")}
          </Text>
          <FeatureTariffInformation feature={feature} translate={translate} />
        </div>
      </div>
    </ThemedModal>
  );
}
